/**
 * @fileoverview Corporate-group linking inside the Grouping and merging section.
 *
 * Surfaces MOR-708 contact groups: shows the contact's group with its
 * members (primary starred), and lets the practitioner
 * - remove the contact from its group,
 * - set the contact as the group's primary (details) contact,
 * - add the contact to an existing group, or
 * - create a new group by pairing this contact with another job contact.
 *
 * Ungrouped contacts behave as singleton groups for automations, so the
 * empty state explains grouping rather than demanding it.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Star, Users, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { SearchableSelect } from "@/ui/composed/inputs/searchable-select";
import type { JobContactRow } from "./types";

interface ContactGroupSectionProps {
  contact: JobContactRow;
  /** Other rows on this job (for the new-group partner picker). */
  jobContacts: Array<JobContactRow>;
  disabled?: boolean;
}

export function ContactGroupSection({
  contact,
  jobContacts,
  disabled,
}: ContactGroupSectionProps) {
  const { data: groups } = useQuery(
    convexQuery(api.contacts.groups.listContactGroups, {
      jobId: contact.jobId,
    }),
  );

  const addToGroup = useConvexMutation(api.contacts.groups.addContactsToGroup);
  const removeFromGroup = useConvexMutation(
    api.contacts.groups.removeContactFromGroup,
  );
  const createGroup = useConvexMutation(api.contacts.groups.createContactGroup);
  const setPrimary = useConvexMutation(
    api.contacts.groups.setGroupPrimaryContact,
  );

  const [mode, setMode] = useState<"idle" | "add" | "create">("idle");
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");
  const [newGroupName, setNewGroupName] = useState("");
  const [partnerContactId, setPartnerContactId] = useState<string>("");

  const currentGroup = groups?.find((g) => g._id === contact.groupId);
  const otherGroups = (groups ?? []).filter((g) => g._id !== contact.groupId);

  const partnerOptions = jobContacts
    .filter((c) => c._id !== contact._id && c.status === "active")
    .map((c) => ({
      value: c._id,
      label: c.displayName,
      title: c.groupName
        ? `${c.displayName} — currently in ${c.groupName}`
        : undefined,
    }));

  const run = async (op: Promise<unknown>, success: string) => {
    try {
      await op;
      toast.success(success);
      setMode("idle");
      setSelectedGroupId("");
      setNewGroupName("");
      setPartnerContactId("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong");
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <h4 className="font-sans text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Corporate group
      </h4>

      {currentGroup ? (
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-muted/20 p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate text-sm font-medium text-foreground">
                {currentGroup.name}
              </span>
              <Badge
                color={currentGroup.origin === "auto" ? "blue" : "gray"}
                variant="soft"
                size="sm"
                className="px-1 py-0 text-[10px]"
              >
                {currentGroup.origin === "auto" ? "Auto" : "Manual"}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 shrink-0 text-xs"
              disabled={disabled}
              onClick={() =>
                run(
                  removeFromGroup({ contactId: contact._id }),
                  `Removed from ${currentGroup.name}`,
                )
              }
            >
              <X className="mr-1 h-3 w-3" />
              Remove
            </Button>
          </div>

          <ul className="flex flex-col gap-1">
            {currentGroup.members.map((member) => {
              const isPrimary = member._id === currentGroup.primaryContactId;
              const isSelf = member._id === contact._id;
              return (
                <li
                  key={member._id}
                  className="flex items-center gap-2 text-xs text-muted-foreground"
                >
                  <Star
                    className={
                      isPrimary
                        ? "h-3 w-3 shrink-0 fill-amber-400 text-amber-400"
                        : "h-3 w-3 shrink-0 text-muted-foreground/30"
                    }
                    aria-label={isPrimary ? "Primary contact" : undefined}
                  />
                  <span className={isSelf ? "font-medium text-foreground" : ""}>
                    {member.displayName}
                  </span>
                </li>
              );
            })}
          </ul>

          {currentGroup.primaryContactId !== contact._id && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 self-start text-xs"
              disabled={disabled}
              onClick={() =>
                run(
                  setPrimary({
                    groupId: currentGroup._id,
                    primaryContactId: contact._id,
                  }),
                  "Set as the group's primary contact",
                )
              }
            >
              <Star className="mr-1 h-3 w-3" />
              Make primary contact
            </Button>
          )}
          <p className="text-[11px] text-muted-foreground/80">
            Documents and automations address the group through its primary
            contact&rsquo;s details; every member stays its own legal entity.
          </p>
        </div>
      ) : mode === "idle" ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-border bg-muted/10 p-3">
          <p className="text-xs text-muted-foreground">
            Not part of a corporate group. Group related entities (e.g. hold co
            / op co under one security registration) so they act as one contact
            in documents.
          </p>
          <div className="flex shrink-0 flex-col gap-1.5">
            {otherGroups.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={disabled}
                onClick={() => setMode("add")}
              >
                Add to group
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              disabled={disabled || partnerOptions.length === 0}
              onClick={() => setMode("create")}
            >
              New group…
            </Button>
          </div>
        </div>
      ) : mode === "add" ? (
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-muted/20 p-3">
          <SearchableSelect
            value={selectedGroupId}
            onValueChange={setSelectedGroupId}
            options={otherGroups.map((g) => ({
              value: g._id,
              label: `${g.name} (${g.members.length})`,
            }))}
            placeholder="Choose a group…"
            searchPlaceholder="Search groups…"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              className="h-7 text-xs"
              disabled={!selectedGroupId}
              onClick={() => {
                const group = otherGroups.find(
                  (g) => g._id === selectedGroupId,
                );
                void run(
                  addToGroup({
                    groupId: selectedGroupId as Id<"contactGroups">,
                    contactIds: [contact._id],
                  }),
                  `Added to ${group?.name ?? "group"}`,
                );
              }}
            >
              Add
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setMode("idle")}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-muted/20 p-3">
          <Input
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder="Group name, e.g. Solaris Group"
            className="h-8 text-sm"
          />
          <SearchableSelect
            value={partnerContactId}
            onValueChange={setPartnerContactId}
            options={partnerOptions}
            placeholder="Add another contact to the group…"
            searchPlaceholder="Search contacts…"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              className="h-7 text-xs"
              disabled={!newGroupName.trim() || !partnerContactId}
              onClick={() =>
                run(
                  createGroup({
                    jobId: contact.jobId,
                    name: newGroupName.trim(),
                    contactIds: [
                      contact._id,
                      partnerContactId as Id<"jobContacts">,
                    ],
                    primaryContactId: contact._id,
                  }),
                  `Created ${newGroupName.trim()}`,
                )
              }
            >
              Create group
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setMode("idle")}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
